import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip,
  CircularProgress,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { neon } from '@neondatabase/serverless';

// Get the database URL
const getDatabaseUrl = () => {
  return 'postgres://neondb_owner:npg_Ub9EiNceZ3ro@ep-divine-sun-a18caqng-pooler.ap-southeast-1.aws.neon.tech/neondb';
};

// Helper function to map database values to display text
const mapValueToLabel = (value) => {
  const valueMap = {
    'excellent': { label: '優良', color: 'success', rate: '>95%' },
    'good': { label: '良好', color: 'primary', rate: '85-95%' },
    'acceptable': { label: '可接受', color: 'info', rate: '70-85%' },
    'needsImprovement': { label: '需改進', color: 'warning', rate: '50-70%' },
    'insufficient': { label: '不足', color: 'error', rate: '<50%' }
  };
  
  return valueMap[value] || { label: value, color: 'default', rate: '' };
};

// Helper function to extract selected issues from JSON object
const getSelectedIssues = (issuesObj) => {
  if (!issuesObj) return [];
  
  try {
    // If the data is a string, parse it
    const issues = typeof issuesObj === 'string' ? JSON.parse(issuesObj) : issuesObj;
    
    // Issue name mappings
    const issueLabels = {
      // Teaching issues
      scriptMismatch: '建議內容與腳本不符',
      impreciseContent: '建議內容不夠精確',
      inappropriateContext: '建議不適用當前情境',
      ambiguousContent: '建議內容語意模糊',
      
      // Node issues
      falsePositive: '誤判判斷為達標',
      falseNegative: '誤判判斷為未達標',
      inconsistentJudgment: '誤判判斷不一致',
      delayedJudgment: '誤判判斷延遲',
      
      // Error correction issues
      missedErrors: '未能識別特定類型錯誤',
      noSuggestions: '未能提供修改建議',
      noContext: '未能提供使用情境',
      unclearSuggestions: '建議過於不明確',
      impracticalSuggestions: '系統建議不實用',
      
      // Checkpoint issues
      falsePass: '查核點判斷為達標',
      falseFail: '查核點判斷未未達標',
      unreasonableDelay: '查核點延遲不合理',
      unclearDelay: '查核點延遲不明確',
      
      // Common
      other: '其他'
    };
    
    // Extract regular selected issues
    const selectedIssues = [];
    
    Object.entries(issues).forEach(([key, value]) => {
      if (key === 'other' && value === true) {
        selectedIssues.push({
          key: 'other',
          label: issueLabels.other,
          isDetails: false
        });
      }
      else if (key !== 'otherDetails' && key !== 'other' && value === true) {
        selectedIssues.push({
          key,
          label: issueLabels[key] || key,
          isDetails: false
        });
      }
    });
    
    // Add other details if the 'other' option is selected
    if (issues.other === true && issues.otherDetails) {
      selectedIssues.push({
        key: 'otherDetails',
        label: issues.otherDetails,
        isDetails: true
      });
    }
    
    return selectedIssues;
  } catch (err) {
    console.error('Error parsing issues:', err);
    return [];
  }
};

const POCEvaluationResults = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [databaseUrl] = useState(() => getDatabaseUrl());
  const [memberId] = useState(() => localStorage.getItem('memberId'));

  // Load evaluations when component mounts
  useEffect(() => {
    fetchEvaluations();
  }, []);

  // Function to fetch evaluations from the database
  const fetchEvaluations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // First check if the table exists
      const tableExists = await sql`
        SELECT EXISTS (
          SELECT FROM information_schema.tables 
          WHERE table_name = 'evaluation_forms'
        );
      `;
      
      if (!tableExists[0].exists) {
        setEvaluations([]);
        return;
      }
      
      // Fetch evaluations filtered by member_id if available
      const results = await sql`
        SELECT 
          ef.*,
          m.username as member_name
        FROM evaluation_forms ef
        LEFT JOIN members m ON ef.member_id = m.id
        WHERE ${memberId ? sql`ef.member_id = ${parseInt(memberId)}` : sql`1=1`}
        ORDER BY ef.created_at DESC
      `;
      
      setEvaluations(results);
      
    } catch (err) {
      console.error('Error fetching evaluations:', err);
      setError(`Failed to fetch evaluations: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    // Create a date object that's explicitly in the Asia/Taipei timezone
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Taipei'
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            POC系統評估結果
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchEvaluations}
            disabled={isLoading}
          >
            刷新資料
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : evaluations.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1">
              尚無評估資料
            </Typography>
          </Paper>
        ) : (
          <>
            {evaluations.map((evaluation) => {
              // Get display info for each rating
              const teachingInfo = mapValueToLabel(evaluation.teaching_accuracy);
              const nodeInfo = mapValueToLabel(evaluation.node_evaluation);
              const errorInfo = mapValueToLabel(evaluation.error_correction_evaluation);
              const checkpointInfo = mapValueToLabel(evaluation.checkpoint_evaluation);
              
              // Extract selected issues
              const teachingIssues = getSelectedIssues(evaluation.teaching_issues);
              const nodeIssues = getSelectedIssues(evaluation.node_issues);
              const errorCorrectionIssues = getSelectedIssues(evaluation.error_correction_issues);
              const checkpointIssues = getSelectedIssues(evaluation.checkpoint_issues);
              
              // Check if there are any selected issues
              const hasIssues = teachingIssues.length > 0 || nodeIssues.length > 0 || 
                               errorCorrectionIssues.length > 0 || checkpointIssues.length > 0;
              
              return (
                <Paper key={evaluation.id} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      評估時間: {formatTimestamp(evaluation.created_at)}
                    </Typography>
                    <Typography variant="body1">
                      {evaluation.member_name ? `提交者: ${evaluation.member_name}` : '提交者: (訪客)'}
                    </Typography>
                  </Box>
                  
                  {evaluation.conversation_id && (
                    <Box sx={{ mb: 2 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => window.open(`/history?view=${evaluation.conversation_id}`, '_blank')}
                      >
                        查看相關對話 #{evaluation.conversation_id}
                      </Button>
                    </Box>
                  )}
                  
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>評估項目</TableCell>
                          <TableCell>評分</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>教學引導準確性</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${teachingInfo.label} (${teachingInfo.rate})`} 
                              color={teachingInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>節點判斷正確性</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${nodeInfo.label} (${nodeInfo.rate})`} 
                              color={nodeInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>評估報告糾錯狀況</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${errorInfo.label} (${errorInfo.rate})`} 
                              color={errorInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>查核點判斷準確性</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${checkpointInfo.label} (${checkpointInfo.rate})`} 
                              color={checkpointInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {hasIssues && (
                    <Accordion sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight="bold">已選擇的問題</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {teachingIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">教學引導問題:</Typography>
                            <List dense disablePadding>
                              {teachingIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? '其他' : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {nodeIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">節點判斷問題:</Typography>
                            <List dense disablePadding>
                              {nodeIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? '其他' : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {errorCorrectionIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">評估報告糾錯問題:</Typography>
                            <List dense disablePadding>
                              {errorCorrectionIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? '其他' : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {checkpointIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">查核點判斷問題:</Typography>
                            <List dense disablePadding>
                              {checkpointIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? '其他' : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}
                  
                  {evaluation.technical_suggestions && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">技術建議:</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="body2">
                          {evaluation.technical_suggestions}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </>
        )}
      </Box>
    </Container>
  );
};

export default POCEvaluationResults; 